import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'disparos:theme';
const VALID: readonly Theme[] = ['light', 'dark', 'system'];

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (VALID as readonly string[]).includes(value);
}

function readStored(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToDom(resolved: 'light' | 'dark'): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/**
 * Hook de tema com persistência em localStorage. Default `system` (segue OS).
 *
 * Aplica `class="dark"` no `<html>` reativamente. CSS vars em `:root` e
 * `.dark` (em `index.css`) cuidam do swap de cores. Tailwind config
 * `darkMode: 'class'` reaproveita pra classes `dark:*`.
 *
 * Quando theme === 'system', listener no `matchMedia` reaplica se OS muda
 * preferência durante a sessão (raro, mas barato cobrir).
 *
 * **FOUC**: pequeno flash de tema claro no primeiro paint quando user
 * escolheu `dark`. Custo aceitável pra MVP — fix completo exige inline
 * script no index.html antes do React montar.
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (next: Theme) => void;
  resolved: 'light' | 'dark';
} {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readStored()));

  useEffect(() => {
    const r = resolveTheme(theme);
    setResolved(r);
    applyToDom(r);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // private mode / quota — não-fatal
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      const r = resolveTheme('system');
      setResolved(r);
      applyToDom(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return { theme, setTheme: setThemeState, resolved };
}
